import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Tooltip,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
  Sailing as SailingIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { api } from '../config/api';

interface POLine {
  line_number: number;
  stock_code: string;
  description: string;
  ordered_qty: number;
  packing_list_qty: number;
  unit: string;
  expected_delivery_by: string;
  chain_of_custody: string;
}

interface POSummary {
  po_number: string;
  supplier_code: string;
  total_lines: number;
  total_ordered_qty: number;
  total_packing_list_qty: number;
  container_count: number;
  status: 'pending' | 'partial' | 'complete' | 'over_shipped';
  variance_percentage: number;
  expected_delivery_by: string | null;  // Changed from earliest_delivery - now grouped by delivery date
  due_diligence_completed: boolean;
}

interface POReconciliationProps {
  searchTerm: string;
  dateFilter: string;
  supplierFilter: string;
  onDataLoad?: (summaries: POSummary[]) => void;
}

// MarineTraffic ETA data structure (cached from vessel_tracking table)
interface MarineTrafficETA {
  eta: string | null;
  vessel: string | null;
  status: string | null;
  // Extended fields from Kpler cache
  eta_status: string | null;
  carrier: string | null;
  bill_of_lading: string | null;
  port_of_loading: string | null;
  departure_date: string | null;
  departure_status: string | null;
  container_numbers: string[] | null;
  vessel_position: { lat: number; lon: number; area: string | null; speed: number | null } | null;
  delay_days: number;
  initial_eta: string | null;
  transshipment_ports: Array<{ name: string; country: string }> | null;
  web_view_link: string | null;
  last_updated: string | null;
  // UI state
  loading: boolean;
  error: string | null;
}

// Generate composite key for PO + delivery date (handles split deliveries)
const getPOKey = (po: POSummary): string => {
  return `${po.po_number}_${po.expected_delivery_by || 'no-date'}`;
};

const POReconciliation: React.FC<POReconciliationProps> = ({ searchTerm, dateFilter, supplierFilter, onDataLoad }) => {
  const [poSummaries, setPOSummaries] = useState<POSummary[]>([]);
  const [poLines, setPOLines] = useState<{ [key: string]: POLine[] }>({});  // Keyed by composite key
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPOs, setExpandedPOs] = useState<Set<string>>(new Set());  // Composite keys
  const [loadingLines, setLoadingLines] = useState<{ [key: string]: boolean }>({});  // Keyed by composite key
  const [marineTrafficETAs, setMarineTrafficETAs] = useState<{ [key: string]: MarineTrafficETA }>({});
  const [vesselRefreshing, setVesselRefreshing] = useState(false);
  const [vesselLastRefreshed, setVesselLastRefreshed] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const handleVesselRefresh = async () => {
    setVesselRefreshing(true);
    try {
      const response = await api.post('/marinetraffic/refresh');
      if (response.data.success) {
        setSnackbar({ open: true, message: `Vessel tracking refreshed — ${response.data.pos_updated} POs updated` });
        // Re-fetch the cached data to update the UI
        await fetchMarineTrafficETAs(poSummaries);
      } else {
        setSnackbar({ open: true, message: response.data.error || 'Refresh failed' });
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Refresh failed';
      setSnackbar({ open: true, message: msg });
    } finally {
      setVesselRefreshing(false);
    }
  };

  const loadPOSummaries = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters from filters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (dateFilter) params.append('date', dateFilter);
      if (supplierFilter) params.append('supplier', supplierFilter);

      const response = await api.get(`/po-reconciliation?${params}`);
      const summaries = response.data.po_summaries || [];
      setPOSummaries(summaries);
      if (onDataLoad) {
        onDataLoad(summaries);
      }
    } catch (err: any) {
      console.error('Error loading PO summaries:', err);
      setError('Failed to load PO reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPOSummaries();
  }, [searchTerm, dateFilter, supplierFilter]);

  // Fetch MarineTraffic ETAs from cached vessel_tracking table
  // Keyed by getPOKey (po_number + delivery date) so each delivery wave of a split PO
  // shows its OWN vessel ETA. The backend maps a PO's shipment ETAs to its waves by date
  // order; we fall back to the per-PO earliest ETA when a wave has no dedicated shipment.
  const fetchMarineTrafficETAs = async (summaries: POSummary[]) => {
    const emptyETA: MarineTrafficETA = {
      eta: null, vessel: null, status: null,
      eta_status: null, carrier: null, bill_of_lading: null,
      port_of_loading: null, departure_date: null, departure_status: null,
      container_numbers: null, vessel_position: null, delay_days: 0,
      initial_eta: null, transshipment_ports: null, web_view_link: null,
      last_updated: null, loading: false, error: null
    };

    const mapEntry = (d: any): MarineTrafficETA => ({
      eta: d.eta, vessel: d.vessel, status: d.status,
      eta_status: d.eta_status, carrier: d.carrier,
      bill_of_lading: d.bill_of_lading,
      port_of_loading: d.port_of_loading,
      departure_date: d.departure_date,
      departure_status: d.departure_status,
      container_numbers: d.container_numbers,
      vessel_position: d.vessel_position,
      delay_days: d.delay_days || 0,
      initial_eta: d.initial_eta,
      transshipment_ports: d.transshipment_ports,
      web_view_link: d.web_view_link,
      last_updated: d.last_updated,
      loading: false, error: null
    });

    // Initialize loading state for all wave rows
    const initialState: { [key: string]: MarineTrafficETA } = {};
    summaries.forEach(po => {
      initialState[getPOKey(po)] = { ...emptyETA, loading: true };
    });
    setMarineTrafficETAs(prev => ({ ...prev, ...initialState }));

    try {
      const response = await api.get(`/marinetraffic/etas`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch vessel tracking data');
      }

      const etas = response.data.etas || {};       // per-PO earliest (fallback)
      const waves = response.data.waves || {};      // per-PO, per-delivery-date
      if (response.data.last_refreshed) {
        setVesselLastRefreshed(response.data.last_refreshed);
      }

      const results: { [key: string]: MarineTrafficETA } = {};
      summaries.forEach(po => {
        const waveEta = po.expected_delivery_by
          ? waves[po.po_number]?.[po.expected_delivery_by]
          : undefined;
        const d = waveEta || etas[po.po_number];
        results[getPOKey(po)] = d ? mapEntry(d) : { ...emptyETA };
      });
      setMarineTrafficETAs(prev => ({ ...prev, ...results }));

    } catch (err: any) {
      console.error('Error fetching vessel tracking data:', err);
      const errorState: { [key: string]: MarineTrafficETA } = {};
      summaries.forEach(po => {
        errorState[getPOKey(po)] = { ...emptyETA, error: err.message };
      });
      setMarineTrafficETAs(prev => ({ ...prev, ...errorState }));
    }
  };

  // Fetch MarineTraffic ETAs when PO summaries load
  useEffect(() => {
    if (poSummaries.length > 0) {
      fetchMarineTrafficETAs(poSummaries);
    }
  }, [poSummaries]);

  const loadPOLines = async (po: POSummary) => {
    const key = getPOKey(po);
    if (poLines[key]) return; // Already loaded

    try {
      setLoadingLines(prev => ({ ...prev, [key]: true }));
      // Build URL with delivery date filter for split delivery POs
      let url = `/po-lines/${po.po_number}`;
      if (po.expected_delivery_by) {
        url += `?expected_delivery_by=${po.expected_delivery_by}`;
      } else {
        url += '?expected_delivery_by=null';  // Filter for lines with NULL delivery date
      }
      const response = await api.get(url);
      setPOLines(prev => ({ ...prev, [key]: response.data.lines || [] }));
    } catch (err) {
      console.error('Error loading PO lines:', err);
    } finally {
      setLoadingLines(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAccordionChange = (po: POSummary) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    const key = getPOKey(po);
    setExpandedPOs(prev => {
      const next = new Set(prev);
      if (isExpanded) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
    if (isExpanded) {
      loadPOLines(po);
    }
  };

  const handleDueDiligenceToggle = async (poNumber: string, currentValue: boolean) => {
    try {
      const response = await api.put(
        `/po/${poNumber}/due-diligence`,
        { due_diligence_completed: !currentValue }
      );

      if (response.data.success) {
        // Update local state - update ALL delivery date groups for this PO
        // (due diligence is PO-level, not delivery-date-level)
        setPOSummaries(prevSummaries =>
          prevSummaries.map(po =>
            po.po_number === poNumber
              ? { ...po, due_diligence_completed: !currentValue }
              : po
          )
        );
      }
    } catch (err: any) {
      console.error('Error updating due diligence:', err);
      alert('Failed to update due diligence status');
    }
  };

  const getStatusChip = (summary: POSummary) => {
    // Use backend's line-by-line status calculation (handles mixed units correctly)
    // pending = no packing list, complete = all lines match 100%, partial = any mismatch
    switch (summary.status) {
      case 'pending':
        return (
          <Chip
            label="No Packing List"
            size="small"
            icon={<HelpIcon />}
            sx={{
              backgroundColor: '#9c27b0',
              color: 'white',
              '& .MuiChip-icon': {
                color: 'white'
              }
            }}
          />
        );
      case 'complete':
        return <Chip label="Match" size="small" color="success" icon={<CheckIcon />} />;
      case 'partial':
        return <Chip label="Mismatch" size="small" color="error" icon={<CancelIcon />} />;
      default:
        return <Chip label="Unknown" size="small" color="default" />;
    }
  };

  const filteredPOs = poSummaries.sort((a, b) => {
    // Sort by expected delivery date (nulls last), then by PO number
    if (!a.expected_delivery_by && !b.expected_delivery_by) {
      return a.po_number.localeCompare(b.po_number);
    }
    if (!a.expected_delivery_by) return 1;
    if (!b.expected_delivery_by) return -1;
    const dateCompare = new Date(a.expected_delivery_by).getTime() - new Date(b.expected_delivery_by).getTime();
    if (dateCompare !== 0) return dateCompare;
    return a.po_number.localeCompare(b.po_number);
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Vessel Tracking Refresh */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1} gap={1}>
        {vesselLastRefreshed && (
          <Typography variant="caption" color="text.secondary">
            Vessel data: {new Date(vesselLastRefreshed).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        )}
        <Tooltip title="Refresh vessel tracking from Kpler (rate limited to once per hour)">
          <span>
            <IconButton
              size="small"
              onClick={handleVesselRefresh}
              disabled={vesselRefreshing}
            >
              {vesselRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* PO List */}
      {filteredPOs.length === 0 ? (
        <Alert severity="info">No purchase orders found</Alert>
      ) : (
        filteredPOs.map((po) => {
          const poKey = getPOKey(po);
          return (
          <Accordion
            key={poKey}
            expanded={expandedPOs.has(poKey)}
            onChange={handleAccordionChange(po)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%" gap={2}>
                <Typography variant="h6" sx={{ minWidth: 120 }}>
                  PO {po.po_number} ({po.supplier_code})
                </Typography>

                {getStatusChip(po)}

                <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={po.due_diligence_completed}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleDueDiligenceToggle(po.po_number, po.due_diligence_completed);
                    }}
                    size="small"
                    sx={{ padding: '4px' }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: po.due_diligence_completed ? '#000000' : '#d32f2f',
                      fontWeight: po.due_diligence_completed ? 500 : 700,
                      fontSize: '0.875rem'
                    }}
                  >
                    Due Diligence Completed
                  </Typography>
                </Box>

                <Box flex={1} />

                {po.expected_delivery_by ? (
                  <Chip
                    label={`Due: ${new Date(po.expected_delivery_by).toLocaleDateString('en-AU', {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}`}
                    size="small"
                    sx={{
                      backgroundColor: '#1976d2',
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                ) : (
                  <Chip
                    label="No delivery date"
                    size="small"
                    sx={{
                      backgroundColor: '#757575',
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                )}

                {/* Vessel Tracking (Kpler) — per delivery wave */}
                {marineTrafficETAs[poKey]?.loading ? (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                ) : marineTrafficETAs[poKey]?.eta ? (
                  (() => {
                    const etaData = marineTrafficETAs[poKey];
                    return (
                      <Tooltip
                        title={
                          <Box sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {etaData.vessel || 'Unknown Vessel'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Status:</strong> {etaData.status || 'Unknown'}
                            </Typography>
                            {etaData.carrier && (
                              <Typography variant="body2">
                                <strong>Carrier:</strong> {etaData.carrier}
                              </Typography>
                            )}
                            {etaData.bill_of_lading && (
                              <Typography variant="body2">
                                <strong>B/L:</strong> {etaData.bill_of_lading}
                              </Typography>
                            )}
                            {etaData.port_of_loading && (
                              <Typography variant="body2">
                                <strong>Origin:</strong> {etaData.port_of_loading}
                                {etaData.departure_date && ` (${etaData.departure_status === 'actual' ? 'departed' : 'departs'} ${new Date(etaData.departure_date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit' })})`}
                              </Typography>
                            )}
                            {etaData.transshipment_ports && etaData.transshipment_ports.length > 0 && (
                              <Typography variant="body2">
                                <strong>Transshipment:</strong> {etaData.transshipment_ports.map(p => p.name).join(' → ')}
                              </Typography>
                            )}
                            {etaData.vessel_position?.area && (
                              <Typography variant="body2">
                                <strong>Location:</strong> {etaData.vessel_position.area}
                                {etaData.vessel_position.speed != null && ` (${etaData.vessel_position.speed} kn)`}
                              </Typography>
                            )}
                            {etaData.delay_days !== 0 && (
                              <Typography variant="body2" sx={{ color: etaData.delay_days > 0 ? '#ff8a80' : '#b9f6ca' }}>
                                <strong>{etaData.delay_days > 0 ? 'Delayed' : 'Ahead'}:</strong> {Math.abs(etaData.delay_days)} day{Math.abs(etaData.delay_days) !== 1 ? 's' : ''}
                              </Typography>
                            )}
                            {etaData.container_numbers && etaData.container_numbers.length > 0 && (
                              <Typography variant="body2">
                                <strong>Containers:</strong> {etaData.container_numbers.length}
                              </Typography>
                            )}
                            {etaData.last_updated && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                                Updated: {new Date(etaData.last_updated).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Chip
                          icon={<SailingIcon sx={{ fontSize: 16 }} />}
                          label={`Vessel ETA${etaData.eta_status && etaData.eta_status !== 'actual' ? ' (est)' : ''}: ${new Date(etaData.eta + 'T00:00:00').toLocaleDateString('en-AU', {
                            weekday: 'short',
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}`}
                          size="small"
                          component={etaData.web_view_link ? 'a' : 'div'}
                          {...(etaData.web_view_link ? { href: etaData.web_view_link, target: '_blank', rel: 'noopener noreferrer' } : {})}
                          clickable={!!etaData.web_view_link}
                          sx={{
                            backgroundColor: etaData.delay_days > 0 ? '#c62828' : '#00695c',
                            color: 'white',
                            fontWeight: 500,
                            '& .MuiChip-icon': {
                              color: 'white'
                            },
                            ...(etaData.web_view_link ? { cursor: 'pointer', '&:hover': { backgroundColor: etaData.delay_days > 0 ? '#b71c1c' : '#004d40' } } : {})
                          }}
                        />
                      </Tooltip>
                    );
                  })()
                ) : null}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Box>
                {/* Line Items Table */}
                {loadingLines[poKey] ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : poLines[poKey] ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Line</TableCell>
                          <TableCell>Stock Code</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Ordered</TableCell>
                          <TableCell align="right">Packing List</TableCell>
                          <TableCell align="center">Match</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Chain of Custody</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {poLines[poKey].map((line) => {
                          const tolerance = line.unit === 'KG' ? 2 : 0;
                          const matches = Math.abs(line.packing_list_qty - line.ordered_qty) <= tolerance;

                          return (
                            <TableRow key={line.line_number}>
                              <TableCell>{line.line_number}</TableCell>
                              <TableCell>{line.stock_code}</TableCell>
                              <TableCell sx={{ maxWidth: 300 }}>{line.description}</TableCell>
                              <TableCell align="right">{line.ordered_qty.toLocaleString()}</TableCell>
                              <TableCell align="right">{line.packing_list_qty.toLocaleString()}</TableCell>
                              <TableCell align="center">
                                {matches ? (
                                  <CheckIcon color="success" fontSize="small" />
                                ) : (
                                  <CancelIcon color="error" fontSize="small" />
                                )}
                              </TableCell>
                              <TableCell>{line.unit}</TableCell>
                              <TableCell>{line.chain_of_custody || '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : null}
              </Box>
            </AccordionDetails>
          </Accordion>
          );
        })
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default POReconciliation;
